
import { PostsContainer } from "./PostsContainer";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Users, Shield } from "lucide-react";

interface TabContentProps {
  selectedCategory: string;
}

export const TabContent = ({ selectedCategory }: TabContentProps) => {
  // Get current user
  const { data: currentUser } = useQuery({
    queryKey: ['current-user'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      return session?.user || null;
    }
  });

  // Get user's friends to create network-based feed
  const { data: friends } = useQuery({
    queryKey: ['user-friends', currentUser?.id],
    queryFn: async () => {
      if (!currentUser?.id) return [];
      
      const { data: friendIds } = await supabase
        .rpc('get_user_friends', { user_id: currentUser.id });

      return friendIds?.map(f => f.friend_id) || [];
    },
    enabled: !!currentUser?.id
  });

  // Show chronological friend feed if user has friends, otherwise show suggested content
  const shouldShowNetworkFeed = friends && friends.length > 0;

  return (
    <div className="w-full space-y-4">
      {shouldShowNetworkFeed && (
        <Card className="bg-gradient-to-r from-primary/10 to-accent/10 border-primary/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Shield className="w-5 h-5 text-primary" />
              <div>
                <h4 className="font-medium">Your Friend Network</h4>
                <p className="text-sm text-muted-foreground">
                  Real updates from your {friends?.length} verified friends
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      <PostsContainer
        queryKey={['posts', selectedCategory, shouldShowNetworkFeed ? 'friends' : 'suggested']}
        category={selectedCategory !== 'all' ? selectedCategory : undefined}
        orderBy={{ column: 'created_at', ascending: false }}
        excludeGroupPosts={true}
        friendsOnly={shouldShowNetworkFeed ? friends : undefined}
        showSuggested={!shouldShowNetworkFeed}
      />
      
      {!shouldShowNetworkFeed && currentUser && (
        <Card className="bg-gradient-to-r from-accent/10 to-secondary/10 border-accent/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Users className="w-5 h-5 text-accent" />
              <div>
                <h4 className="font-medium">Discover & Connect</h4>
                <p className="text-sm text-muted-foreground">
                  Showing suggested content while you build your friend network
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export const TrendingTabContent = () => {
  return (
    <div className="w-full">
      <PostsContainer
        queryKey={['trending-posts']}
        orderBy={{ column: 'likes_count', ascending: false }}
        limit={10}
        excludeGroupPosts={true}
      />
    </div>
  );
};

export const SupportTabContent = () => {
  return (
    <div className="w-full">
      <PostsContainer
        queryKey={['support-posts']}
        category="support"
        orderBy={{ column: 'created_at', ascending: false }}
        excludeGroupPosts={true}
      />
    </div>
  );
};
